/**
 * Single source of truth for the CasaOthers knowledge-base taxonomy.
 *
 * Why this file exists separately:
 *   - The frontend renders the category grid, the admin form's dropdown, AND
 *     the per-category landing pages from this same list. Defining it once
 *     means adding a new top-level topic is a one-line change.
 *   - The slug (`id`) is what gets stored in MongoDB on each blog post, so it
 *     MUST be URL-safe and stable. Don't rename slugs once a post uses them.
 *   - `tagSuggestions` is purely a hint for the admin (auto-complete style) —
 *     users can still type any tag they want.
 *
 * Translation strategy:
 *   `label` is Dutch (primary audience), `labelEn` falls back when isNL=false.
 *   `intro` is the long-form intro text shown on the category landing page;
 *   keeping it inline saves a round-trip to a CMS while we have a small
 *   number of categories.
 */
const CATEGORIES = [
  {
    id: 'kopen-in-spanje',
    label: 'Kopen in Spanje',
    labelEn: 'Buying in Spain',
    icon: 'Home',
    intro:
      'Alles wat u moet weten over het kopen van een woning in Spanje: het koopproces, hypotheken, nieuwbouw versus bestaande bouw, kosten koper en de juridische kant. Onmisbaar voor iedere Nederlandse of Belgische koper.',
    introEn:
      'Everything you need to know about buying a property in Spain: the purchase process, mortgages, new build vs resale, buyer costs and the legal side. Essential reading for any Dutch or Belgian buyer.',
    tagSuggestions: [
      'koopproces', 'nieuwbouw', 'bestaande bouw', 'hypotheek',
      'kosten koper', 'belastingen', 'juridisch', 'notaris',
    ],
  },
  {
    id: 'investeren',
    label: 'Investeren',
    labelEn: 'Investing',
    icon: 'TrendingUp',
    intro:
      'Spanje is een van de meest aantrekkelijke markten voor vastgoedinvesteringen in Europa. Lees over rendementen, verhuurmogelijkheden, marktontwikkelingen en hoe u een tweede woning slim laat renderen.',
    introEn:
      'Spain is one of the most attractive real estate investment markets in Europe. Discover yields, rental opportunities, market trends, and how to make your second home work for you.',
    tagSuggestions: [
      'rendement', 'verhuur', 'tweede woning', 'marktontwikkelingen',
      'vastgoedinvesteringen', 'roi',
    ],
  },
  {
    id: 'emigreren',
    label: 'Emigreren',
    labelEn: 'Emigrating',
    icon: 'Plane',
    intro:
      'Een nieuw leven aan de Spaanse kust begint met goede voorbereiding. NIE, residentie, zorgverzekering, scholen voor de kinderen, pensioenregelingen — wij leggen alles helder uit.',
    introEn:
      'A new life on the Spanish coast starts with thorough preparation. NIE, residency, healthcare, schools for the kids, pension arrangements — we explain it all clearly.',
    tagSuggestions: [
      'nie', 'residentie', 'verhuizen', 'zorg', 'pensioen',
      'scholen', 'belastingen',
    ],
  },
  {
    id: 'regios',
    label: 'Regio\u2019s',
    labelEn: 'Regions',
    icon: 'MapPin',
    intro:
      'Costa Blanca, Costa C\u00e1lida en Costa Almer\u00eda. Elke kust heeft zijn eigen karakter, klimaat en levensstijl. Ontdek welke regio bij u past en welke steden en dorpen u moet kennen.',
    introEn:
      'Costa Blanca, Costa C\u00e1lida and Costa Almer\u00eda. Each coast has its own character, climate and lifestyle. Find the region that suits you best and the towns you need to know.',
    tagSuggestions: ['costa blanca', 'costa calida', 'costa almeria'],
  },
  {
    id: 'lifestyle',
    label: 'Lifestyle',
    labelEn: 'Lifestyle',
    icon: 'Coffee',
    intro:
      'Het Spaanse leven is meer dan alleen vastgoed. Golf, mooie tuinen, zonnige terrassen, fietsen door het binnenland, de beste stranden en restaurants — laat u inspireren door wat Spanje te bieden heeft.',
    introEn:
      'Spanish living is more than just real estate. Golf, beautiful gardens, sun-drenched terraces, cycling through the interior, the best beaches and restaurants — get inspired by everything Spain has to offer.',
    tagSuggestions: [
      'golf', 'interieur', 'zwembad', 'tuin', 'restaurants',
      'stranden', 'natuur', 'wandelen', 'fietsen',
    ],
  },
  {
    id: 'praktische-gidsen',
    label: 'Praktische Gidsen',
    labelEn: 'Practical Guides',
    icon: 'BookOpen',
    intro:
      'Concrete how-to artikelen voor het dagelijks leven in Spanje: een bankrekening openen, internet aansluiten, verzekeringen kiezen, een verbouwing aanpakken en energierekeningen begrijpen.',
    introEn:
      'Practical how-to articles for daily life in Spain: opening a bank account, getting internet installed, choosing insurance, renovating your home and understanding energy bills.',
    tagSuggestions: [
      'verzekeringen', 'notaris', 'bankrekening', 'verbouwen',
      'energie', 'internet',
    ],
  },
];

export const CATEGORY_BY_ID = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));
export const CATEGORY_IDS = CATEGORIES.map(c => c.id);

export default CATEGORIES;
